import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { SimpleSelect } from "./DepotField";
import { api, formatApiError, PRODUCT_CATEGORIES, UNITS } from "../lib/api";

const EMPTY = { sku: "", name: "", category: "General", unit: "pcs", cost_price: "", sell_price: "", reorder_level: "" };

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="label-mono">{label}</Label>{children}</div>;
}

export function ProductDialog({ open, onOpenChange, product, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setForm(product ? { ...product } : EMPTY);
    }
  }, [open, product]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...form, cost_price: Number(form.cost_price || 0), sell_price: Number(form.sell_price || 0), reorder_level: Number(form.reorder_level || 0) };
    try {
      if (product) await api.put(`/products/${product.id}`, body);
      else await api.post("/products", body);
      toast.success(product ? "Product updated" : "Product added");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="product-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{product ? "Edit product" : "Add product"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <Field label="SKU"><Input required value={form.sku} onChange={set("sku")} data-testid="product-sku-input" placeholder="e.g. HAM-16OZ" /></Field>
          <SimpleSelect label="Unit" value={form.unit} onChange={set("unit")} options={UNITS} testid="product-unit-select" />
          <div className="col-span-2"><Field label="Name"><Input required value={form.name} onChange={set("name")} data-testid="product-name-input" /></Field></div>
          <div className="col-span-2">
            <Field label="Category">
              <Input list="category-options" value={form.category} onChange={set("category")} data-testid="product-category-input" />
              <datalist id="category-options">{PRODUCT_CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist>
            </Field>
          </div>
          <Field label="Cost price (TSh)"><Input type="number" step="0.01" min="0" value={form.cost_price} onChange={set("cost_price")} data-testid="product-cost-input" /></Field>
          <Field label="Selling price (TSh)"><Input type="number" step="0.01" min="0" value={form.sell_price} onChange={set("sell_price")} data-testid="product-price-input" /></Field>
          <Field label="Reorder level"><Input type="number" step="any" min="0" value={form.reorder_level} onChange={set("reorder_level")} data-testid="product-reorder-input" /></Field>
          <DialogFooter className="col-span-2 mt-2">
            <Button type="submit" disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="product-save-button">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save product
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
